import {
  BodyText,
  Button,
  ButtonGroup,
  Modal,
} from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useTransferringCallContext } from '../context';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import Arrow from './arrow.svg';
import { TransferringCallItem } from './TransferringCallItem';
import styles from './TransferringCallConfirmation.module.css';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';
import React from 'react';
import { inboxLocators } from '@locators/inbox';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';

export const TransferringCallConfirmation = ({
  handleBack,
}: {
  handleBack: () => void;
}) => {
  const [isTransferring, setIsTransferring] = useState(false);
  const {
    handleTransfer,
    handleWarmTransfer,
    from,
    to,
    transferType,
    onClose,
  } = useTransferringCallContext();
  const {
    [FeatureFlagKeys.featureInboxCallWarmTransfers]:
      featureInboxCallWarmTransfers,
    [FeatureFlagKeys.featureInboxCallTransferToQueue]:
      featureInboxCallTransferToQueue,
  } = useFeatureFlags();

  const onTransfer = useCallback(() => {
    try {
      setIsTransferring(true);
      handleTransfer(to.number);
    } catch (error) {
      console.error('Error transferring call', error);
    } finally {
      setIsTransferring(false);
    }
  }, [handleTransfer, to]);

  const onWarmTransfer = useCallback(() => {
    try {
      setIsTransferring(true);
      handleWarmTransfer(to.number);
    } catch (error) {
      console.error('Error warm transferring call', error);
    } finally {
      onClose();
      setIsTransferring(false);
    }
  }, [handleWarmTransfer, to, onClose]);

  const transferNumber = maskPhoneNumber(to?.number);
  const fromNumber = maskPhoneNumber(from?.number);
  const isTransferringToQueue =
    featureInboxCallTransferToQueue &&
    transferType === OutboundCallEntity.Queue;

  return (
    <Modal
      open
      focusTrapOptions={{ disabled: true }}
      portal={false}
      title={'Call Transfer'}
      onClose={onClose}
      size={ModalSizes.S}
      animate={false}
      footerAlign='space-between'
      id={inboxLocators.callBar.transferConfirmationModal}
      footer={
        featureInboxCallWarmTransfers ? (
          <React.Fragment>
            <Button
              onClick={handleBack}
              outline
              id={inboxLocators.callBar.transferConfirmationModalBackBtn}
            >
              Back
            </Button>
            <ButtonGroup>
              <Button
                id={inboxLocators.callBar.transferConfirmationModalCallFirstBtn}
                disabled={isTransferring}
                onClick={onWarmTransfer}
              >
                Call First
              </Button>
              <Button
                primary
                id={inboxLocators.callBar.transferConfirmationModalTransferBtn}
                disabled={isTransferring}
                onClick={onTransfer}
                loading={isTransferring}
              >
                Transfer Now
              </Button>
            </ButtonGroup>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button
              onClick={handleBack}
              outline
              id={inboxLocators.callBar.transferConfirmationModalBackBtn}
            >
              Back
            </Button>
            <Button
              primary
              id={inboxLocators.callBar.transferConfirmationModalTransferBtn}
              disabled={isTransferring}
              onClick={onTransfer}
              loading={isTransferring}
            >
              Transfer Now
            </Button>
          </React.Fragment>
        )
      }
    >
      <div className={styles.content}>
        <BodyText className={styles.title}>
          {`Would you like to transfer the call to ${isTransferringToQueue ? to.name : transferNumber} ?`}
        </BodyText>
        <div className={styles.container}>
          <TransferringCallItem
            item={{
              ...from,
              number: fromNumber,
            }}
          />
          <div className={classNames(styles.item, styles.arrow)}>
            <Arrow />
          </div>
          <TransferringCallItem
            item={{ ...to, number: transferNumber }}
            transferType={transferType}
          />
        </div>
      </div>
    </Modal>
  );
};
